import { FlaskConical, Loader2, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const ABTesterTab = ({
  abVariantA, setAbVariantA,
  abVariantB, setAbVariantB,
  runningAbTest, abTestResults,
  onRunAbTest,
  credits, AB_TEST_COST
}) => (
  <div className="space-y-6">
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600">
            <FlaskConical className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">A/B Content Tester</h2>
            <p className="text-gray-500 text-sm">Test 2 ad variants and find the winner</p>
          </div>
        </div>
        <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-medium">{AB_TEST_COST} credits</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Label className="text-gray-700 dark:text-gray-300 font-medium">Variant A</Label>
          <Textarea 
            placeholder="Enter your first ad copy / caption variant..."
            value={abVariantA}
            onChange={(e) => setAbVariantA(e.target.value)}
            className="min-h-[120px]"
          />
        </div>
        <div className="space-y-4">
          <Label className="text-gray-700 dark:text-gray-300 font-medium">Variant B</Label>
          <Textarea 
            placeholder="Enter your second ad copy / caption variant..."
            value={abVariantB}
            onChange={(e) => setAbVariantB(e.target.value)}
            className="min-h-[120px]"
          />
        </div>
      </div>

      <div className="mt-6">
        <Button 
          onClick={onRunAbTest}
          disabled={runningAbTest || credits < AB_TEST_COST}
          className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 py-6 text-lg"
        >
          {runningAbTest ? (
            <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Running A/B Test...</>
          ) : (
            <><FlaskConical className="h-5 w-5 mr-2" />Run A/B Test ({AB_TEST_COST} credits)</>
          )}
        </Button>
      </div>

      {abTestResults && (
        <div className="mt-6 p-6 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200">
          <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-violet-600" />
            Test Results
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {["A", "B"].map((variant) => {
              const data = variant === "A" ? abTestResults.variantA : abTestResults.variantB;
              const isWinner = abTestResults.winner === variant;
              return (
                <div key={variant} className={`p-4 rounded-lg ${isWinner ? "bg-green-100 border-2 border-green-500" : "bg-white"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Variant {variant}</span>
                    {isWinner && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">WINNER</span>}
                  </div>
                  <div className="text-2xl font-bold text-gray-800 dark:text-white">{data.ctr}%</div>
                  <div className="text-xs text-gray-500">CTR ({data.clicks} clicks / {data.impressions} impressions)</div>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 bg-white p-3 rounded-lg">
            <strong>Recommendation:</strong> Use Variant {abTestResults.winner} for better engagement. It achieved {abTestResults.winner === "A" ? abTestResults.variantA.ctr : abTestResults.variantB.ctr}% click-through rate.
          </p>
        </div>
      )}
    </div>
  </div>
);
